/**
 * At least some browsers do not respect the `accept` attribute on file inputs
 * when using drag and drop so this function helps manual validation.
 * @param {string} accept - The value of the `accept` attribute on a file input.
 * It must be a comma-separated list of MIME types (including wildcards like
 * `image/*`) or file extensions like `.png`.
 *
 * Ref:
 * <https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#limiting_accepted_file_types>
 * @returns {{type: RegExp[], name: RegExp[]}} - An object with two arrays of
 * regular expressions, one for MIME types and one for file name extensions.
 * @example
 * // returns { type: [/^image\/.*$/i], name: [/\.png$/i, /\.jpg$/i] }
 * parseAcceptAttribute('image/*, .png, .jpg');
 */
export const parseAcceptAttribute = accept =>
    accept.split(',').reduce(
        (acc, type) => {
            const trimmed = type.trim();
            if (trimmed.startsWith('.')) {
                // file name extension
                const formatted = trimmed.replace('.', '\\.');
                // eslint-disable-next-line security/detect-non-literal-regexp
                acc.name.push(new RegExp(`${formatted}$`, 'i'));
            } else {
                // MIME type
                const formatted = trimmed.replace('*', '.*');
                // eslint-disable-next-line security/detect-non-literal-regexp
                acc.type.push(new RegExp(`^${formatted}$`, 'i'));
            }

            return acc;
        },
        { type: [], name: [] }
    );

/**
 * Validate a file against the `accept` attribute on a file input.
 * @param {File} file - The file to validate.
 * @param {string} accept - The value of the `accept` attribute on a file input.
 * See {@link parseAcceptAttribute}.
 * @returns {boolean} - Whether the file is valid.
 * @example
 * // returns true
 * validateFile(new File([''], 'image.png'), '.png');
 */
export const validateFile = (file, accept) => {
    const acceptChecks = parseAcceptAttribute(accept);

    return (
        acceptChecks.type.some(type => type.test(file.type)) ||
        acceptChecks.name.some(name => name.test(file.name))
    );
};

/**
 * Converts a file size in bytes to megabytes (MB).
 *
 * @param {number} size - The size of the file in bytes.
 * @returns {number} The size of the file in megabytes.
 * @example
 * // returns 1
 * calculateSizeInMB(1048576);
 */
export const calculateSizeInMB = size => size / 1024 / 1024;

/**
 * Converts a File object to a Base64-encoded data URI string.
 *
 * @param {File} file - The file to convert.
 * @returns {Promise<string>} A promise that resolves to the Base64 data URI of the file.
 * @example
 * // returns 'data:image/png;base64,...'
 * // fileToBase64DataUri(file).then(dataUri => { ... });
 */
export const fileToBase64DataUri = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
